import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Group, Select, Stack, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { sortBy } from "lodash-es";
import { z } from "zod";

import {
  getGetCategoriesOptionallyfilteredbylocationQueryKey,
  useCreateCategory,
} from "@/shared/api/generated/categories";
import { useGetLocations } from "@/shared/api/generated/locations";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    name: z.string().min(1, { message: t`Velg navn` }),
    location: z.string().min(1, { message: t`Velg lokasjon` }),
  });
}

type NewCategoryFormSchema = z.infer<ReturnType<typeof useFormSchema>>;

export default function NewCategoryForm({ close }: { close: () => void }) {
  const { t } = useLingui();

  const { museumId } = useParams({ strict: false });

  const formSchema = useFormSchema();

  const form = useForm<NewCategoryFormSchema>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      location: museumId ?? "",
    },
    validate: zodResolver(formSchema),
  });

  const queryClient = useQueryClient();

  const { mutateAsync: createCategory } = useCreateCategory();

  async function handleSubmit(values: NewCategoryFormSchema) {
    await createCategory(
      {
        data: {
          name: values.name,
          locationId: values.location,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetCategoriesOptionallyfilteredbylocationQueryKey(),
          });
          close();
        },
      },
    );
  }

  const { data: locations } = useGetLocations();

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label={t`Navn`}
          placeholder={t`Velg navn`}
          withAsterisk
          {...form.getInputProps("name")}
        />

        {!museumId && (
          <Select
            label={t`Lokasjon`}
            placeholder={t`Velg lokasjon`}
            data={sortBy(
              locations?.data?.map((location) => ({
                value: location.id,
                label: location.name,
              })),
              (o) => o.label.toLowerCase(),
            )}
            searchable
            clearable
            withAsterisk
            {...form.getInputProps("location")}
          />
        )}

        <Group justify="end" gap={8} mt={8}>
          <Button variant="subtle" onClick={close}>
            <Trans>Avbryt</Trans>
          </Button>
          <Button type="submit">
            <Trans>Opprett</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
